import { useEffect, useState } from "react";
import api from "@/lib/api";
import EditorLayout from "@/components/editor/EditorLayout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Save, Info, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const Section = ({ title, children }) => (
  <div className="bg-white border border-[#E5E2D9] rounded-2xl p-8 mb-6">
    <h2 className="font-bold text-lg mb-5">{title}</h2>
    <div className="space-y-5">{children}</div>
  </div>
);

const Field = ({ label, help, children }) => (
  <div>
    <label className="block text-sm font-semibold mb-1.5">{label}</label>
    {children}
    {help && <p className="text-xs text-[#8a8f89] mt-1.5 leading-relaxed">{help}</p>}
  </div>
);

export default function SettingsPage() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try { await api.put("/settings", s); toast.success("ההגדרות נשמרו"); }
    catch { toast.error("השמירה נכשלה"); }
    finally { setSaving(false); }
  };

  if (!s) return <EditorLayout><div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#4A5D4E] border-t-transparent rounded-full animate-spin" /></div></EditorLayout>;

  const pixelValid = !s.meta_pixel_id || /^\d{5,}$/.test(s.meta_pixel_id);
  const gtmValid = !s.gtm_id || /^GTM-[A-Z0-9]+$/i.test(s.gtm_id);

  return (
    <EditorLayout>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold">הגדרות ומעקב</h1>
          <p className="text-[#5C635C] mt-1">פרטי המותג, רשתות חברתיות וכלי מעקב.</p>
        </div>
        <Button data-testid="settings-save-btn" onClick={save} disabled={saving || !pixelValid || !gtmValid} className="bg-[#4A5D4E] hover:bg-[#3d4d41] rounded-full gap-2">
          <Save className="w-4 h-4" /> {saving ? "שומר..." : "שמירה"}
        </Button>
      </div>

      <Section title="פרטי האתר">
        <Field label="שם האתר / המותג"><Input data-testid="set-site-title" value={s.site_title} onChange={(e) => set("site_title", e.target.value)} /></Field>
        <Field label="תיאור קצר" help="משפט אחד שמתאר את המותג. מופיע בתחתית האתר ובחיפוש בגוגל.">
          <Textarea data-testid="set-site-desc" value={s.site_description} rows={2} onChange={(e) => set("site_description", e.target.value)} />
        </Field>
        <Field label="אימייל ליצירת קשר"><Input data-testid="set-contact-email" dir="ltr" value={s.contact_email} onChange={(e) => set("contact_email", e.target.value)} /></Field>
      </Section>

      <Section title="רשתות חברתיות וחנות">
        <Field label="קישור לחנות Etsy" help="יופיע ככפתור בולט בראש האתר."><Input data-testid="set-etsy" dir="ltr" value={s.social_etsy} onChange={(e) => set("social_etsy", e.target.value)} placeholder="https://etsy.com/shop/..." /></Field>
        <Field label="Instagram"><Input dir="ltr" data-testid="set-instagram" value={s.social_instagram} onChange={(e) => set("social_instagram", e.target.value)} placeholder="https://instagram.com/..." /></Field>
        <Field label="Facebook"><Input dir="ltr" data-testid="set-facebook" value={s.social_facebook} onChange={(e) => set("social_facebook", e.target.value)} placeholder="https://facebook.com/..." /></Field>
        <Field label="YouTube"><Input dir="ltr" data-testid="set-youtube" value={s.social_youtube} onChange={(e) => set("social_youtube", e.target.value)} placeholder="https://youtube.com/..." /></Field>
        <Field label="TikTok"><Input dir="ltr" data-testid="set-tiktok" value={s.social_tiktok} onChange={(e) => set("social_tiktok", e.target.value)} placeholder="https://tiktok.com/@..." /></Field>
      </Section>

      <Section title="מעקב ומדידה (Tracking)">
        <div className="flex items-start gap-2 bg-[#F2F5F1] rounded-lg p-4 text-sm text-[#5C635C]">
          <Info className="w-4 h-4 mt-0.5 shrink-0 text-[#4A5D4E]" />
          <div>
            <p className="font-semibold text-[#4A5D4E] mb-1">מה זה בכלל?</p>
            אלה כלים שמאפשרים לראות כמה אנשים מבקרים באתר ומאיפה הגיעו. <b>צריך להזין רק מזהה (ID), לא קוד.</b> אם אין לך — אפשר להשאיר ריק. אם את לא בטוחה, כדאי להתייעץ עם איש/אשת שיווק.
          </div>
        </div>
        <Field label="Meta Pixel ID" help="מזהה מספרי בלבד (לדוגמה: 123456789012345). מגיע מ-Facebook / Meta Ads.">
          <Input data-testid="set-pixel" dir="ltr" value={s.meta_pixel_id} onChange={(e) => set("meta_pixel_id", e.target.value.trim())} placeholder="123456789012345" />
          {s.meta_pixel_id && (
            <p className={`text-xs mt-1.5 flex items-center gap-1 ${pixelValid ? "text-green-700" : "text-[#8C3A3A]"}`}>
              {pixelValid ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
              {pixelValid ? "המזהה נראה תקין" : "מזהה לא תקין — צריך מספרים בלבד"}
            </p>
          )}
        </Field>
        <Field label="Google Tag Manager ID" help='מתחיל תמיד ב-"GTM-" (לדוגמה: GTM-ABC1234).'>
          <Input data-testid="set-gtm" dir="ltr" value={s.gtm_id} onChange={(e) => set("gtm_id", e.target.value.trim())} placeholder="GTM-XXXXXXX" />
          {s.gtm_id && (
            <p className={`text-xs mt-1.5 flex items-center gap-1 ${gtmValid ? "text-green-700" : "text-[#8C3A3A]"}`}>
              {gtmValid ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
              {gtmValid ? "המזהה נראה תקין" : 'מזהה לא תקין — חייב להתחיל ב-"GTM-"'}
            </p>
          )}
        </Field>
      </Section>
    </EditorLayout>
  );
}
